import { AdsFilteringType } from '../../../types/ads';
import { LocationModel } from '../../../types/location';
import type { AdsSearchParams } from '../utils/adsSearchParams';

type LocationDisplaySearchParams = Pick<
  AdsSearchParams,
  | 'addressParamAddress'
  | 'addressParamLat'
  | 'addressParamLong'
  | 'addressParamName'
  | 'addressParamPlaceId'
>;

const getLocationIds = (locationId: AdsFilteringType['locationId']): string[] => {
  if (!locationId) return [];
  return Array.isArray(locationId) ? locationId : [locationId];
};

const hasSameLocationIds = (
  currentLocationId: AdsSearchParams['locationId'],
  nextLocationId: AdsFilteringType['locationId']
) => {
  const currentIds = getLocationIds(currentLocationId);
  const nextIds = getLocationIds(nextLocationId);

  return (
    nextIds.length > 0 &&
    currentIds.length === nextIds.length &&
    currentIds.every((id) => nextIds.includes(id))
  );
};

const getConfirmedLocationId = (locationId: AdsFilteringType['locationId']) =>
  Array.isArray(locationId) ? (locationId.length ? locationId : undefined) : locationId;

const getSelectedLocationLabel = (selectedLocation: LocationModel) =>
  selectedLocation[selectedLocation.kind] ?? '';

export const clearLocationFilter = (filteringObj: AdsFilteringType): AdsFilteringType => ({
  ...filteringObj,
  address: [],
  locationId: undefined,
});

export const getPreservedLocationDisplayParams = (
  currentParams: AdsSearchParams,
  nextFilteringObj: AdsFilteringType
): Partial<LocationDisplaySearchParams> => {
  if (
    nextFilteringObj.address?.length ||
    !hasSameLocationIds(currentParams.locationId, nextFilteringObj.locationId)
  ) {
    return {};
  }

  return {
    addressParamAddress: currentParams.addressParamAddress,
    addressParamLat: currentParams.addressParamLat,
    addressParamLong: currentParams.addressParamLong,
    addressParamName: currentParams.addressParamName,
    addressParamPlaceId: currentParams.addressParamPlaceId,
  };
};

export const getLocationSearchFilter = ({
  filteringObj,
  inputAddressValue,
  selectedLocation,
}: {
  filteringObj: AdsFilteringType;
  inputAddressValue: string;
  selectedLocation: LocationModel | null;
}): AdsFilteringType => {
  const locationId = selectedLocation?.id ?? getConfirmedLocationId(filteringObj.locationId);
  const locationLabel =
    (selectedLocation ? getSelectedLocationLabel(selectedLocation) : '') ||
    inputAddressValue.trim();

  if (selectedLocation || locationId) {
    return {
      ...filteringObj,
      address: locationLabel
        ? [
            {
              addressEn: locationLabel,
              nameEn: locationLabel,
            },
          ]
        : [],
      locationId,
    };
  }

  return {
    ...filteringObj,
    address: filteringObj.address?.length ? filteringObj.address : [],
    locationId: undefined,
  };
};
